/**
 * JSON/Data diff utilities
 * Compares two data structures and returns differences
 */

export type DiffType = 'added' | 'removed' | 'modified' | 'unchanged'

export interface DiffEntry {
  path: string
  type: DiffType
  oldValue?: unknown
  newValue?: unknown
}

export interface DiffResult {
  entries: DiffEntry[]
  summary: {
    added: number
    removed: number
    modified: number
    unchanged: number
    total: number
  }
  similarity: number
}

/**
 * Compare two data structures and return differences
 */
export function diffData(
  oldData: unknown,
  newData: unknown,
  path: string = '$'
): DiffEntry[] {
  const entries: DiffEntry[] = []

  function compare(oldVal: unknown, newVal: unknown, currentPath: string): void {
    // Both null/undefined
    if (oldVal === undefined && newVal === undefined) return
    if (oldVal === null && newVal === null) {
      entries.push({ path: currentPath, type: 'unchanged', oldValue: oldVal, newValue: newVal })
      return
    }

    // One is undefined (property added or removed)
    if (oldVal === undefined) {
      entries.push({ path: currentPath, type: 'added', newValue: newVal })
      return
    }

    if (newVal === undefined) {
      entries.push({ path: currentPath, type: 'removed', oldValue: oldVal })
      return
    }

    // Different types
    if (typeof oldVal !== typeof newVal) {
      entries.push({ path: currentPath, type: 'modified', oldValue: oldVal, newValue: newVal })
      return
    }

    // Primitives
    if (typeof oldVal !== 'object' || oldVal === null) {
      if (oldVal === newVal) {
        entries.push({ path: currentPath, type: 'unchanged', oldValue: oldVal, newValue: newVal })
      } else {
        entries.push({ path: currentPath, type: 'modified', oldValue: oldVal, newValue: newVal })
      }
      return
    }

    // Arrays
    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      const maxLen = Math.max(oldVal.length, newVal.length)

      for (let i = 0; i < maxLen; i++) {
        compare(oldVal[i], newVal[i], `${currentPath}[${i}]`)
      }
      return
    }

    // One is array, other is not
    if (Array.isArray(oldVal) || Array.isArray(newVal)) {
      entries.push({ path: currentPath, type: 'modified', oldValue: oldVal, newValue: newVal })
      return
    }

    // Objects
    const oldObj = oldVal as Record<string, unknown>
    const newObj = newVal as Record<string, unknown>
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

    for (const key of allKeys) {
      compare(oldObj[key], newObj[key], `${currentPath}.${key}`)
    }
  }

  compare(oldData, newData, path)

  return entries
}

/**
 * Calculate diff result with summary
 */
export function calculateDiff(oldData: unknown, newData: unknown): DiffResult {
  const entries = diffData(oldData, newData)

  const summary = {
    added: 0,
    removed: 0,
    modified: 0,
    unchanged: 0,
    total: entries.length
  }

  for (const entry of entries) {
    summary[entry.type]++
  }

  // Calculate similarity percentage
  const changedCount = summary.added + summary.removed + summary.modified
  const similarity = summary.total > 0
    ? Math.round(((summary.total - changedCount) / summary.total) * 100)
    : 100

  return { entries, summary, similarity }
}

/**
 * Get only changed entries (excluding unchanged)
 */
export function getChangedEntries(result: DiffResult): DiffEntry[] {
  return result.entries.filter(e => e.type !== 'unchanged')
}

/**
 * Get entries by type
 */
export function getEntriesByType(result: DiffResult, type: DiffType): DiffEntry[] {
  return result.entries.filter(e => e.type === type)
}

/**
 * Get a map of paths to their diff type for visual highlighting
 */
export function getDiffPathMap(result: DiffResult): Map<string, DiffType> {
  const map = new Map<string, DiffType>()

  for (const entry of result.entries) {
    map.set(entry.path, entry.type)
  }

  return map
}

/**
 * Check if a path has any difference (including children)
 */
export function pathHasDiff(path: string, pathMap: Map<string, DiffType>): DiffType | null {
  // Check exact match
  if (pathMap.has(path)) {
    const type = pathMap.get(path)!
    if (type !== 'unchanged') return type
  }

  // Check if any child has a difference
  for (const [entryPath, type] of pathMap) {
    if (type !== 'unchanged') {
      if (entryPath.startsWith(path + '.') || entryPath.startsWith(path + '[')) {
        return 'modified' // Parent of changed node shows as modified
      }
    }
  }

  return null
}

/**
 * Format a value for display
 */
export function formatDiffValue(value: unknown): string {
  if (value === undefined) return 'undefined'
  if (value === null) return 'null'
  if (typeof value === 'string') return `"${value}"`
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

/**
 * Get color class for diff type
 */
export function getDiffColor(type: DiffType): string {
  switch (type) {
    case 'added': return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
    case 'removed': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
    case 'modified': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
    case 'unchanged': return 'text-slate-500 dark:text-slate-400'
    default: return ''
  }
}

/**
 * Get icon for diff type
 */
export function getDiffIcon(type: DiffType): string {
  switch (type) {
    case 'added': return '+'
    case 'removed': return '-'
    case 'modified': return '~'
    case 'unchanged': return '='
    default: return ''
  }
}
